import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation, useSearchParams, Link } from "react-router-dom";
import { AlertTriangle, Loader2, ArrowRight, EyeOff, Eye } from "lucide-react";
import toast from "react-hot-toast";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useSelector } from "react-redux";

import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import AuthService from "@/services/authService";
// DISABLED-FEATURE: browser extension is switched off — see server/app/api/v2/api.py.
// import ExtensionService from "@/services/extensionService";
import RHFInput from "@/components/form/RHFInput";
import { Card } from "@/components/ui/Card";
import { Button, ButtonShadcn } from "@/components/ui/Button";
import { Checkbox } from "@/components/ui/Checkbox";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { adLoginSchema, localSigninSchema, adLoginValue, localLoginValue } from "./validation";
import { CORPORATE_LOGIN_ENABLED, EMAIL_LOGIN_ENABLED, DEMO_DISCLAIMER_ENABLED } from "@/config/authFeatures";
import { TenantSettingsService } from "@/services/tenantSettingsService";
import { CenteredSpinner } from "@/components/shared/Loader";
import { usePlatformDomain } from "@/hooks/usePlatformDomain";
import { useBranding } from "@/hooks/useBranding";
import { resolveLandingPath, isRestorableTarget } from "@/routes/landing";
// import { MicrosoftIcon } from "../microsoft-callback";

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { branding } = useBranding();

  // Platform SuperAdmin subdomain (platform.<domain>) — auth still goes
  // through the same /auth/signin + /auth/login endpoints; the backend
  // branches on the platform slug and hits the ``superadmins`` table.
  const { isPlatform } = usePlatformDomain();

  const currentTheme = useSelector((state) => state.theme.theme);
  const isDark = currentTheme === "dark";

  const { login, isLoading } = useAuth();

  const [showPassword, setShowPassword] = useState(false);
  const [isMicrosoftLoading, setIsMicrosoftLoading] = useState(false);
  // Unset until the server has answered — see `mode` below.
  const [mode, setMode] = useState(null);

  // Whether THIS tenant signs in through Active Directory, asked per tenant
  // rather than read off a build-time constant naming one of them. `settled`
  // is tracked separately from the answer because "we do not know yet" and
  // "the answer is no" must render differently.
  const [ad, setAd] = useState({ enabled: false, settled: false });

  // Where they were headed before being bounced here, if anything. Validated
  // against the signed-in account below — a captured deep link is only useful
  // if that account may actually open it.
  const requestedPath = location.state?.from?.pathname;

  useEffect(() => {
    // The platform subdomain is not a tenant: `get_public_db` rejects the
    // reserved slug, so the call could only fail. SuperAdmins sign in through
    // AD there, so it is treated as AD-capable without asking — unless the
    // deployment has corporate login switched off, which `/auth/login` refuses
    // outright. Assuming AD there left the SuperAdmin with the one form the
    // route cannot accept and no toggle to leave it.
    if (isPlatform) {
      setAd({ enabled: CORPORATE_LOGIN_ENABLED, settled: true });
      return;
    }

    let cancelled = false;
    TenantSettingsService.getPublicSettings()
      .then((res) => {
        if (!cancelled) setAd({ enabled: !!res?.data?.adLoginEnabled, settled: true });
      })
      .catch((err) => {
        if (cancelled) return;

        // Fail towards offering AD only when we could not ASK — a network
        // blip, an offline browser, a dead gateway. There the two mistakes
        // are not the same size: an AD form shown to an email tenant costs
        // one refused sign-in and a toast that moves the user to the right
        // form (see the submit handler below), while an email form shown to
        // an AD tenant costs every employee their only way in.
        //
        // A reply with a status is the server answering, and it is not a
        // blip: a suspended tenant 403s this read on every single load, so
        // failing towards AD there showed an email tenant the corporate form
        // for as long as the suspension lasted. The recovery path cannot
        // help — it only fires once someone has typed a password and
        // submitted it. So a server that answered is taken at its word, and
        // the page opens on the email form.
        //
        // The deployment switch still wins: with corporate login off there
        // is no AD form to fail towards.
        const serverAnswered = !!err?.response;
        setAd({ enabled: !serverAnswered && CORPORATE_LOGIN_ENABLED, settled: true });
      });

    return () => {
      cancelled = true;
    };
  }, [isPlatform]);

  // AD sign-in is available when the server says this tenant uses it, or on
  // the reserved ``platform`` subdomain (the backend routes signed-in AD
  // identities against the ``superadmins`` table for that slug).
  //
  // `CORPORATE_LOGIN_ENABLED` is deliberately NOT folded in here: the server
  // already ANDed the deployment switch into the flag it returned, so doing it
  // again would be the same truth spelled twice. It still governs the toggle's
  // own render condition and the both-disabled notice below.
  const canUseAd = ad.enabled;
  // Resolved once, from the server's answer, so the form never swaps under the
  // cursor. `mode` stays null while unsettled, which is what holds the spinner.
  const resolvedMode = mode ?? (ad.settled ? (canUseAd ? "ad" : "local") : null);
  const isAdMode = canUseAd && resolvedMode === "ad";

  const methods = useForm({
    resolver: zodResolver(isAdMode ? adLoginSchema : localSigninSchema),
    mode: "onChange",
    defaultValues: isAdMode ? adLoginValue : localLoginValue,
  });

  useEffect(() => {
    methods.reset({
      ...(isAdMode ? adLoginValue : localLoginValue),
      ...(DEMO_DISCLAIMER_ENABLED && {
        acknowledged: false,
      }),
    });
  }, [isAdMode]);

  const brandDisplayName = branding?.displayName;

  useEffect(() => {
    const suffix = brandDisplayName || "IMS People Possible AI";
    document.title = isPlatform ? "Platform Sign in | IMS People Possible AI" : `Sign in | ${suffix}`;
  }, [isPlatform, brandDisplayName]);

  // If the user landed here via a forced logout, show the contextual toast
  // and strip the param so a manual refresh doesn't re-toast the same thing.
  useEffect(() => {
    const reason = searchParams.get("reason");
    if (!reason) return;

    if (reason === "org_suspended") {
      toast.error("Your organization account has been deactivated. Please contact your administrator.");
    }

    if (reason === "user_deactivated") {
      toast.error("Your account has been deactivated. Please contact your administrator.");
    }

    if (reason === "user_deactivated") {
      toast.error(
        "Your account has been deactivated. Please contact your administrator.",
      );
    }

    const next = new URLSearchParams(searchParams);
    next.delete("reason");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onHandleSubmit = methods.handleSubmit(async (data) => {
    const result = await login({
      mode: isAdMode ? "ad" : "local",
      password: data.password,
      acknowledged: !!data.acknowledged,
      ...(isAdMode ? { employeeId: data.employeeId } : { email: data.email }),
    });

    if (!result.success) {
      toast.error(result?.error?.message || result?.error?.error_code || (typeof result?.error === "string" ? result.error : "Invalid credentials"));

      // The page offered the wrong form — a failed settings call that fell
      // back to AD, or a tenant whose flag was switched off while this page
      // sat open. Move them to the one that will work rather than leaving
      // them to retype the same credentials into the same refusal.
      // `AD_LOGIN_NOT_ENABLED` is the tenant's answer, `LOGIN_METHOD_DISABLED`
      // the deployment's. Either way the corporate form cannot succeed.
      if (["AD_LOGIN_NOT_ENABLED", "LOGIN_METHOD_DISABLED"].includes(result?.error?.error_code)) setMode("local");
      return;
    }

    // Resolve the destination from the account that just signed in, rather
    // than sending everyone to /admin/dashboard and letting the guards bounce
    // them onward. A SuperAdmin no longer sees the tenant shell flash first,
    // and a user without dashboard permission no longer lands on
    // "Unauthorized" immediately after a successful sign-in.
    const signedInUser = result?.data?.user;
    const target = isRestorableTarget(requestedPath, signedInUser) ? requestedPath : resolveLandingPath(signedInUser);

    toast.success("Welcome back!");

    // DISABLED-FEATURE: browser extension is switched off — see server/app/api/v2/api.py.
    // // Fire-and-forget: if the Chrome extension is installed in this browser,
    // // push the fresh JWT to it so its long-lived extension token gets
    // // refreshed without the user having to open the popup. Silently no-ops
    // // when the extension isn't installed.
    // (async () => {
    //   try {
    //     const meta = await ExtensionService.waitForExtension(1200);
    //     if (!meta) {
    //       console.warn("[TalentFlow ext] login push skipped — bridge.js not detected on this page.");
    //       return;
    //     }
    //     const jwt = AuthService.getAccessToken();
    //     if (!jwt) return;
    //     const res = await ExtensionService.pushJwt(jwt);
    //     if (!res || !res.ok) console.warn("[TalentFlow ext] login JWT push failed:", res);
    //     else console.log("[TalentFlow ext] login JWT push OK:", res);
    //   } catch (err) {
    //     // never block navigation on extension errors
    //     console.warn("[TalentFlow ext] login push threw:", err);
    //   }
    // })();

    navigate(target, { replace: true });
  });

  const anyLoading = isLoading || isMicrosoftLoading;

  // When both methods are disabled (operator misconfiguration), show a warning
  // alongside the fallback email form so the operator knows to fix their config.
  const bothDisabled = !CORPORATE_LOGIN_ENABLED && !EMAIL_LOGIN_ENABLED;

  return (
    <div className="fixed inset-0 overflow-hidden">
      <div
        className={cn(
          "h-full w-full flex items-center justify-center bg-cover bg-center bg-no-repeat px-4",
          isDark ? "bg-[url('/images/home_page_bg_dark.jpg')]" : "bg-[url('/images/home_page_bg_light.jpg')]",
        )}
      >
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm pointer-events-none" aria-hidden />

        <div className="relative z-10 w-full max-w-[420px] sm:max-w-[520px]">
          <Card
            className={cn(
              "mx-auto flex flex-col rounded-2xl border border-white/25 bg-white/10",
              "backdrop-blur-2xl shadow-[0_20px_80px_rgba(0,0,0,0.45)]",
              "px-4 py-4 sm:px-6 sm:py-6",
              "max-h-[92vh] overflow-y-auto scrollbar-hide",
            )}
          >
            <div className="flex flex-col gap-3">
              <header className="mb-1 text-center">
                <h1 className="mt-2 text-lg sm:text-xl font-medium text-text-primary">
                  {brandDisplayName ? `Welcome to ${brandDisplayName}` : "Welcome to IMS People Possible AI"}
                </h1>
                <p className="mt-1 text-[11px] sm:text-xs text-text-primary">
                  {isAdMode ? "Sign in with your Corporate ID and Password." : "Sign in with your Email and Password."}
                </p>
              </header>

              {!ad.settled && <CenteredSpinner className="h-40" />}

              {ad.settled && CORPORATE_LOGIN_ENABLED && EMAIL_LOGIN_ENABLED && canUseAd && (
                <SegmentedControl
                  ariaLabel="Sign-in method"
                  variant="pill"
                  className="mb-1"
                  // The resolved answer, not the click: `mode` is null until
                  // someone picks, so highlighting from it left an AD tenant
                  // looking at the corporate form with neither option marked.
                  value={resolvedMode}
                  onChange={setMode}
                  options={[
                    { value: "ad", label: "Corporate Login" },
                    { value: "local", label: "Email Login" },
                  ]}
                />
              )}

              {/* Misconfiguration notice when both methods are disabled */}
              {ad.settled && bothDisabled && <p className="text-center text-[11px] text-amber-400/80">No login method is enabled. Contact your administrator.</p>}

              {ad.settled && (
                <FormProvider {...methods}>
                  <form onSubmit={onHandleSubmit} className="space-y-3 pt-1 text-sm">
                    {isAdMode ? (
                      <RHFInput
                        name="employeeId"
                        label="Corporate ID"
                        placeholder="Enter Corporate ID"
                        type="text"
                        labelClass="text-text-primary"
                        inputClass="placeholder:text-text-secondary"
                      />
                    ) : (
                      <RHFInput
                        name="email"
                        label="Email ID"
                        placeholder="Enter Email ID"
                        type="text"
                        labelClass="text-text-primary"
                        inputClass="placeholder:text-text-secondary"
                      />
                    )}

                    <div>
                      <div className="relative">
                        <RHFInput
                          name="password"
                          label="Password"
                          placeholder="Enter password"
                          type={showPassword ? "text" : "password"}
                          labelClass="text-text-primary"
                          inputClass="placeholder:text-text-secondary"
                        />

                        {/* The kit button, not a hand-drawn one: the focus
                          ring, the disabled state and `type="button"` all
                          come with it, and `AcceptInviteForm` already draws
                          this same control that way. Icon-only, so the
                          `aria-label` is its whole accessible name. */}
                        <ButtonShadcn
                          variant="ghost"
                          size="icon-xs"
                          onClick={() => setShowPassword((prev) => !prev)}
                          className="absolute right-2 top-[46px] -translate-y-1/2 text-text-secondary hover:text-text-primary"
                          aria-label={showPassword ? "Hide password" : "Show password"}
                        >
                          {showPassword ? <EyeOff size={16} strokeWidth={2} /> : <Eye size={16} strokeWidth={2} />}
                        </ButtonShadcn>
                      </div>

                      {/* Forgot-password link shown on the email tab only.
                        AD credentials aren't managed by us, so an AD user
                        resetting a password does it in AD itself. */}
                      {!isAdMode && (
                        <div className="mt-1 flex justify-end">
                          <Link to="/forgot-password" className="pt-1 pb-2 pr-0 text-xs text-text-primary hover:text-text-primary/60 transition-colors underline">
                            Forgot password?
                          </Link>
                        </div>
                      )}
                    </div>

                    {/* Demo disclaimer — only rendered when DEMO_DISCLAIMER_ENABLED */}
                    {DEMO_DISCLAIMER_ENABLED && (
                      <div className="rounded-lg border border-yellow-300 bg-yellow-100 p-3 space-y-2">
                        <div className="flex items-center gap-2 text-yellow-800">
                          <AlertTriangle size={16} strokeWidth={2} />
                          <span className="text-xs font-semibold uppercase tracking-wide">Demo Environment</span>
                        </div>
                        <ul className="text-[11px] text-black space-y-1 pl-1 list-disc list-inside">
                          <li>Do not upload real, personal, or confidential documents or data.</li>
                          <li>Any submissions may be visible to other users and deleted without notice.</li>
                          <li>This platform is not intended for live or production use.</li>
                        </ul>
                        {/* The kit checkbox. It is not an `<input>`, so it is
                          driven by `Controller` rather than `register`, and
                          named by `htmlFor`/`id` rather than by the label
                          wrapping it. */}
                        <div className="flex items-center gap-2.5 mt-1">
                          <Controller
                            name="acknowledged"
                            control={methods.control}
                            defaultValue={false}
                            render={({ field }) => (
                              <Checkbox
                                id="acknowledged"
                                checked={!!field.value}
                                onCheckedChange={field.onChange}
                                onBlur={field.onBlur}
                                disabled={anyLoading}
                              />
                            )}
                          />
                          <label htmlFor="acknowledged" className="text-[11px] text-black select-none cursor-pointer">
                            I understand and agree to these terms.
                          </label>
                        </div>
                      </div>
                    )}

                    <Button
                      type="submit"
                      disabled={anyLoading || (DEMO_DISCLAIMER_ENABLED && !methods.formState.isValid)}
                      className="mt-1 w-full rounded-lg bg-gradient-to-r from-indigo-500 to-blue-600
                               hover:from-indigo-600 hover:to-blue-700 text-text-base
                               flex items-center justify-center gap-2
                               py-2.5 text-sm transition-all duration-200
                               disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {isLoading ? (
                        <>
                          <Loader2 size={16} className="animate-spin" />
                          Signing in...
                        </>
                      ) : (
                        <>
                          Sign in <ArrowRight size={16} />
                        </>
                      )}
                    </Button>
                  </form>
                </FormProvider>
              )}
            </div>

            {/*
            Uncomment this block when Microsoft login is enabled in UI.

            <div className="mt-4 pt-2">
              <div className="flex items-center gap-3 text-[11px] sm:text-xs text-white/70">
                <div className="flex-1 h-px bg-white/15" />
                <span className="inline-flex items-center rounded-full border border-white/20 bg-black/35 px-4 py-0.5 text-[11px] text-white/75 backdrop-blur-md">
                  or continue with
                </span>
                <div className="flex-1 h-px bg-white/15" />
              </div>

              <div className="mt-4 grid grid-cols-1 gap-2 sm:gap-2.5">
                <Button
                  type="button"
                  onClick={handleMicrosoftLogin}
                  disabled={anyLoading}
                  className="relative flex items-center justify-center gap-1.5
                             rounded-md border border-white/30 bg-white/90
                             hover:bg-white text-slate-700 hover:text-slate-900
                             py-1.5 text-[11px] sm:text-xs transition-all duration-200
                             disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isMicrosoftLoading ? (
                    <>
                      <Loader2
                        size={14}
                        className="animate-spin text-slate-500"
                      />
                      <span>Microsoft</span>
                    </>
                  ) : (
                    <>
                      <span className="flex items-center">
                        <MicrosoftIcon />
                      </span>
                      <span>Microsoft</span>
                    </>
                  )}
                </Button>
              </div>
            </div>
            */}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Login;
